import { ChangeDetectionStrategy, Component, computed, effect, inject, input, linkedSignal, output } from '@angular/core';
import {
	formatDurationUrenMinuten,
	generateCssClassForString,
	getMaandLabelKort,
	getMaandLabelLang,
	isDefined,
	prettyCount,
} from '@cumlaude/shared-utils';
import { DisplayService } from '../../../services/display.service';
import { AttrPath, Interventielaag } from '@cumlaude/metadata';
import { interventielaagForPerc, JaarMaand } from '../../../services/schoolaanwezigheid';
import { QueryParamStateService } from '../../../services/query-param-state.service';
import {
	BasicFilterExpression,
	CompoundFilterExpression,
	DataService,
	FilterDynamicSelectionExpression,
	FilterExpression,
	SchoolaanwezigheidDetails,
} from '../../../services/data.service';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { delay, filter, map, switchMap } from 'rxjs/operators';
import { isUndefined } from 'lodash-es';
import { AanwezigheidUitsplitsing } from '../../../services/weergave-opties';
import { IncrementalListComponent } from '../../../shared/components/incremental-list/incremental-list.component';
import { PasfotoComponent } from '../../../shared/components/pasfoto/pasfoto.component';
import { getFormattedValue } from '../../../shared/components/table/table/table.model';
import { IconComponent } from '@cumlaude/shared-components-icons';
import { TooltipDirective, TooltipType } from '@cumlaude/shared-components-overlays';
import { UrlService } from '../../../services/url.service';
import { CssSpinnerComponent } from '../../../layout/css-spinner/css-spinner.component';
import { AsyncPipe } from '@angular/common';
import { formatDecimal } from '@cumlaude/shared-pipes';

@Component({
	selector: 'app-interventielaag-list-panel',
	templateUrl: './interventielaag-list-panel.component.html',
	styleUrls: ['./interventielaag-list-panel.component.scss'],
	imports: [IconComponent, IncrementalListComponent, PasfotoComponent, TooltipDirective, CssSpinnerComponent, AsyncPipe],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InterventielaagListPanelComponent {
	protected readonly dataService = inject(DataService);
	protected readonly displayService = inject(DisplayService);
	protected readonly qp = inject(QueryParamStateService);
	protected readonly urlService = inject(UrlService);

	protected readonly maxListHeightPx = 615;

	laagFilter = input.required<FilterExpression | undefined>();
	pageFilter = input.required<FilterExpression | undefined>();
	laag = input.required<Interventielaag>(); //  verandert nooit
	maanden = input.required<JaarMaand[]>(); // verandert alleen als filter verandert
	sortMonth = input.required<JaarMaand | undefined>();
	exportMode = input<boolean>(false); // Schakel export modus in/uit
	aanwezigheiduitsplitsing = this.qp.signal_aanwezigheiduitsplitsing();

	totalChanged = output<number | undefined>();

	itemHeightPx = computed(() => {
		switch (this.aanwezigheiduitsplitsing()) {
			case AanwezigheidUitsplitsing.LEERLING:
				return 56;
			default:
				return 40;
		}
	});

	sortDir = linkedSignal<'asc' | 'desc'>(() => defaultSortering(this.laag()));

	offset = linkedSignal(() => {
		this.laagFilter();
		this.pageFilter();
		this.aanwezigheiduitsplitsing();
		this.sortDir();
		this.exportMode(); // Reset offset when export mode changes
		return 0;
	});

	total = linkedSignal<number | undefined>(() => {
		const laagFilter = this.laagFilter();
		this.pageFilter();
		this.aanwezigheiduitsplitsing();
		this.sortDir();
		this.exportMode(); // Reset total when export mode changes
		return laagFilter ? undefined : 0;
	});

	spinnerDelay = toObservable(this.total).pipe(
		delay(200),
		map((x) => true)
	);

	elementen = linkedSignal<SchoolaanwezigheidDetails[]>(() => {
		this.laagFilter();
		this.pageFilter();
		this.aanwezigheiduitsplitsing();
		this.sortDir();
		this.exportMode(); // Reset elements when export mode changes
		return [];
	});

	requestParams = computed(() => {
		const laagFilter = this.laagFilter();
		const pageFilter = this.pageFilter();
		const maanden = this.maanden();
		this.exportMode(); // Recompute when export mode changes
		const maandFilter =
			maanden.length > 0
				? new CompoundFilterExpression(
						maanden.map((jm) => new CompoundFilterExpression([new BasicFilterExpression(['aw_nr_jaar'], jm.nr_jaar), new BasicFilterExpression(['aw_nr_maand'], jm.nr_maand)])),
						'or'
					)
				: undefined;
		const f = pageFilter && maandFilter ? new CompoundFilterExpression([pageFilter, maandFilter]) : pageFilter ?? maandFilter;
		return { laagFilter, f, off: this.offset(), g: uitsplitsingAttr(this.aanwezigheiduitsplitsing()), sortdir: this.sortDir() };
	});

	constructor() {
		effect(() => {
			this.totalChanged.emit(this.total());
		});

		toObservable(this.requestParams)
			.pipe(
				filter(({ laagFilter, off }) => !isUndefined(laagFilter) && (this.exportMode() || this.isOnvolledig(off))),
				switchMap(({ laagFilter, f, g, off, sortdir }) => {
					const limit = this.exportMode() ? this.total() : 100;
					const offset = this.exportMode() ? 0 : off;

					return this.dataService.getSchoolAanwezigheidDetails({
						f,
						laagFilter,
						g,
						month: this.maanden(),
						lim: limit,
						off: offset,
						sortmonth: this.sortMonth(),
						sortdir,
					});
				}),
				takeUntilDestroyed()
			)
			.subscribe((resp) => {
				if (this.exportMode()) {
					// In export mode, replace all data at once
					this.elementen.set(resp.rows);
				} else {
					// Normal mode: append data
					this.elementen.update((val) => [...val, ...resp.rows]);
				}
				if (isDefined(resp.total)) this.total.set(resp.total);
			});
	}

	getTooltip(element: SchoolaanwezigheidDetails, jm: JaarMaand): TooltipType {
		const { nr_maand, voorlopig } = jm;
		const frac = this.getPerc(element, nr_maand);
		const aanwezig = this.getAanwezigMinuten(element, nr_maand);
		const gemist = this.getGemistMinuten(element, nr_maand);
		const totaalLesuren = this.getTotaalLesuren(element, nr_maand);
		const aanwezigLesuren = this.getAanwezigLesuren(element, nr_maand);

		if (frac === null) return 'Geen data beschikbaar';

		return [
			{ label: '', bold: true, value: getMaandLabelLang(nr_maand) + (voorlopig ? ' (voorlopig)' : '') },
			`${getFormattedValue(frac, 'percentage', '1.2-2')} (${interventielaagForPerc(frac)})`,
			`${formatDecimal(aanwezigLesuren)} / ${formatDecimal(totaalLesuren)} lesuren`,
			`${formatDurationUrenMinuten(aanwezig!, 'hu mm')} aanwezig`,
			`${formatDurationUrenMinuten(gemist!, 'hu mm')} gemist`,
		];
	}

	isOnvolledig(offset: number) {
		const total = this.total();
		return isUndefined(total) || offset < total;
	}

	isSortMonth(jm: JaarMaand) {
		return jm.nr_maand === this.sortMonth()?.nr_maand;
	}

	calculateHeight() {
		const total = this.total();
		if (total == 0 || isUndefined(total)) return `${this.itemHeightPx() + 16}px`;

		// In export mode, render all items without height limit
		if (this.exportMode()) {
			return `${total * this.itemHeightPx() + 16}px`;
		}

		return `${Math.min(total * this.itemHeightPx() + 16, this.maxListHeightPx)}px`;
	}

	getEmptyState() {
		switch (this.aanwezigheiduitsplitsing()) {
			case AanwezigheidUitsplitsing.LEERLING:
				return 'Geen leerlingen';
			case AanwezigheidUitsplitsing.KLAS:
				return 'Geen klassen';
			case AanwezigheidUitsplitsing.VAK:
				return 'Geen vakken';
		}
	}

	getPerc(element: SchoolaanwezigheidDetails, nr_maand: string): number | null {
		return this.getByMaandFromElement(element, 'aw_m_perc_aanwezig', nr_maand);
	}

	getAanwezigMinuten(element: SchoolaanwezigheidDetails, nr_maand: string) {
		return this.getByMaandFromElement(element, 'aw_m_nr_aanwezig', nr_maand);
	}

	getGemistMinuten(element: SchoolaanwezigheidDetails, nr_maand: string) {
		return this.getByMaandFromElement(element, 'aw_m_nr_gemist', nr_maand);
	}

	getAanwezigLesuren(element: SchoolaanwezigheidDetails, nr_maand: string) {
		return this.getByMaandFromElement(element, 'aw_m_nr_lesuren_aanwezig', nr_maand);
	}

	getTotaalLesuren(element: SchoolaanwezigheidDetails, nr_maand: string) {
		return this.getByMaandFromElement(element, 'aw_m_nr_lesuren', nr_maand);
	}

	private getByMaandFromElement(element: SchoolaanwezigheidDetails, measureName: string, nr_maand: string) {
		return element[`${measureName}-${nr_maand}` as keyof SchoolaanwezigheidDetails] as number | null;
	}

	getPercFormatted(element: SchoolaanwezigheidDetails, nr_maand: string) {
		const perc = this.getPerc(element, nr_maand);
		return getFormattedValue(perc, 'percentage', '1.0-0|-');
	}

	getPercClass(element: SchoolaanwezigheidDetails, nr_maand: string) {
		const perc = this.getPerc(element, nr_maand);
		if (perc == null) return 'onbekend';
		return generateCssClassForString(interventielaagForPerc(perc));
	}

	toggleSort($last: boolean) {
		if ($last) {
			this.sortDir.update((x) => (x === 'asc' ? 'desc' : 'asc'));
		}
	}

	getTeller() {
		const nr = this.total();
		if (isUndefined(nr)) return '';
		switch (this.aanwezigheiduitsplitsing()) {
			case AanwezigheidUitsplitsing.LEERLING:
				return prettyCount(nr, 'lln.', 'lln.');
			case AanwezigheidUitsplitsing.KLAS:
				return prettyCount(nr, 'klas', 'klassen');
			case AanwezigheidUitsplitsing.VAK:
				return prettyCount(nr, 'vak', 'vakken');
		}
	}

	navigateTo(element: SchoolaanwezigheidDetails) {
		const { laagFilter, f } = this.requestParams();
		const { aw_nr_leerling, aw_nm_klas, aw_fun_nm_vak } = element;
		if (aw_nr_leerling) {
			const mergedFilter = new CompoundFilterExpression([f, laagFilter].filter(isDefined));
			const selection = {
				detailUrl: `/details/leerling/aanwezigheid?selectie=${aw_nr_leerling}`,
				mergeQueryParams: false,
				expression: new FilterDynamicSelectionExpression('schoolaanwezigheid', mergedFilter),
				size: this.total()!,
			};
			this.urlService.navigateToSelection(selection);
		} else if (aw_nm_klas) {
			this.urlService.redirect([], { aw_nm_klas: JSON.stringify([aw_nm_klas]) });
		} else if (aw_fun_nm_vak) {
			this.urlService.redirect([], { aw_fun_nm_vak: JSON.stringify([aw_fun_nm_vak]) });
		}
	}

	protected readonly generateCssClassForString = generateCssClassForString;
	protected readonly getMaandLabelKort = getMaandLabelKort;
	protected readonly AanwezigheidUitsplitsing = AanwezigheidUitsplitsing;
}

function uitsplitsingAttr(uitsplitsing: AanwezigheidUitsplitsing): AttrPath {
	switch (uitsplitsing) {
		case AanwezigheidUitsplitsing.LEERLING:
			return ['aw_nr_leerling'];
		case AanwezigheidUitsplitsing.KLAS:
			return ['aw_nm_klas'];
		case AanwezigheidUitsplitsing.VAK:
			return ['aw_fun_nm_vak'];
	}
}

function defaultSortering(laag: Interventielaag): 'asc' | 'desc' {
	switch (laag) {
		case Interventielaag.AANHOUDEND_VERZUIM:
			return 'asc';
		case Interventielaag.BEGINNEND_VERZUIM:
			return 'asc';
		case Interventielaag.GOEDE_AANWEZIGHEID:
			return 'desc';
	}
}
